// Programmatic SEO topic library.
// Each entry produces an indexable page at /learn/:slug with FAQ + Article JSON-LD.
// Long-tail MS queries chosen for search intent, not medical advice.

export type LearnTopicCategory = "symptom" | "medication" | "wearable" | "lifestyle";

export interface LearnTopic {
  slug: string;
  category: LearnTopicCategory;
  title: string;                    // H1 + <title> (keep <60 chars)
  description: string;              // meta description (<160 chars)
  intro: string;                    // opening paragraph
  keyPoints: string[];              // 3–5 scannable bullets
  correlation: string;              // observed pattern users report
  actionable: string[];             // 3–4 practical suggestions
  faqs: { question: string; answer: string }[];
  relatedSlugs?: string[];
}

export const LEARN_TOPICS: LearnTopic[] = [
  {
    slug: "ms-fatigue-and-heat-sensitivity",
    category: "symptom",
    title: "MS Fatigue and Heat Sensitivity: The Daily Pattern",
    description: "Why heat drains energy fast in MS, what the pattern looks like across a day, and how a daily forecast helps you plan around it.",
    intro: "For most people with MS, a two-degree rise in core body temperature is enough to slow nerve conduction in demyelinated pathways. That's why a warm shower, a summer errand, or a stressful meeting can produce fatigue that feels wildly out of proportion to the activity.",
    keyPoints: [
      "Roughly 60–80% of people with MS report heat sensitivity (Uhthoff's phenomenon).",
      "The dip usually starts 20–40 minutes after the temperature exposure and peaks 1–2 hours later.",
      "Recovery is faster with active cooling than passive rest.",
      "Predictable heat windows are easier to plan around than random fatigue.",
    ],
    correlation: "Users who tag afternoon crashes in check-ins most often show a paired signal: outdoor exposure or a warm room in the previous two hours.",
    actionable: [
      "Pre-cool before, not after: cold drink, cool vest, or cold-water foot bath 20 minutes before a demanding block.",
      "Shift high-focus work into your morning window (typically 8:30–11:30 a.m. for this cohort).",
      "Log warm-shower timing, many users find shifting to a lukewarm evening rinse recovers 1–2 hours of next-day energy.",
      "Use a daily forecast to schedule errands on cooler forecast hours, not by convenience.",
    ],
    faqs: [
      { question: "Is heat sensitivity permanent damage?", answer: "No. Uhthoff's phenomenon is a temporary, reversible slowing of nerve conduction. Symptoms return to baseline once core temperature drops. It's not a relapse and it's not new damage." },
      { question: "How cold is 'cold enough' for a cooling vest?", answer: "Most cooling vests target 55–65°F at the skin. That's cool enough to blunt Uhthoff's without triggering shivering, which itself costs energy." },
      { question: "Does air conditioning really move the needle?", answer: "Yes, indoor temperature under 72°F is one of the most consistent predictors of a smaller afternoon dip in the aggregated cohort." },
    ],
    relatedSlugs: ["ms-fatigue-and-sleep", "cooling-strategies-for-ms"],
  },
  {
    slug: "ms-fatigue-and-sleep",
    category: "symptom",
    title: "MS Fatigue and Sleep: Why 8 Hours Isn't Enough",
    description: "Sleep quality, not just duration, is the single biggest lever on next-day MS energy. Here's what the data shows.",
    intro: "In the aggregated user data, sleep quality correlates more strongly with next-day fatigue than sleep duration does. An eight-hour night with three awakenings is worse than a seven-hour night with one.",
    keyPoints: [
      "Fragmentation matters more than total time in bed.",
      "Spasticity, bladder urgency, and restless legs are the three most common MS-specific disruptors.",
      "A consistent bedtime (±30 min) outperforms a longer but variable sleep window.",
      "Sleep-tracking wearables give you the objective signal you can't self-report accurately.",
    ],
    correlation: "Users with wearables see a 0.4–0.6 Pearson correlation between prior-night deep sleep minutes and next-day PROMIS Fatigue-MS 8a score.",
    actionable: [
      "Fix bedtime before you fix bedtime routine, consistency is the highest-leverage change.",
      "Rule out sleep apnea if fatigue is severe; MS raises the risk and CPAP treatment is transformative for people who need it.",
      "Discuss spasticity treatment with your neurology team if it wakes you more than twice a week.",
      "Track sleep alongside daily check-ins for two weeks, the pattern usually reveals itself.",
    ],
    faqs: [
      { question: "Does napping help or hurt MS fatigue?", answer: "Short (20–30 min) naps before 3 p.m. usually help without disrupting overnight sleep. Longer or later naps commonly push bedtime later and worsen next-day fragmentation." },
      { question: "Should I use melatonin?", answer: "Low-dose (0.3–1 mg) melatonin 30 minutes before target bedtime helps consistency in some users. Discuss with your team, especially if you take DMTs that affect metabolism." },
    ],
    relatedSlugs: ["ms-fatigue-and-heat-sensitivity"],
  },
  {
    slug: "cooling-strategies-for-ms",
    category: "lifestyle",
    title: "Cooling Strategies for MS: What Actually Works",
    description: "A practical guide to cooling vests, foot baths, and pre-cooling protocols, ranked by observed effect in a real MS cohort.",
    intro: "Cooling isn't optional for most people with MS. The question is which method to use, when, and for how long, because they're not equivalent.",
    keyPoints: [
      "Active cooling (vest, cold immersion) works faster than passive cooling (AC alone).",
      "Feet and head lose heat fastest; targeting them beats a lukewarm ambient reduction.",
      "Pre-cooling before an activity outperforms post-cooling recovery.",
    ],
    correlation: "Cohort users who log pre-cooling before a walk retain 22% more afternoon energy on average than users who don't.",
    actionable: [
      "For daily errands: cooling neck wrap or scarf (cheap, effective).",
      "For summer activity: cooling vest for 20–30 min before, not during.",
      "For a heat wave: cool-water foot bath at midday.",
      "For evening recovery: lukewarm rinse and elevate feet.",
    ],
    faqs: [
      { question: "Are cooling vests worth the cost?", answer: "For heat-sensitive users who leave the house in summer, yes, the average payback in retained energy is measured in weeks, not months." },
      { question: "Does drinking cold water actually help?", answer: "A small amount, yes. Ice water lowers core temperature slightly through the gastric route and adds hydration, which is separately protective." },
    ],
    relatedSlugs: ["ms-fatigue-and-heat-sensitivity"],
  },
  {
    slug: "ocrelizumab-and-fatigue",
    category: "medication",
    title: "Ocrelizumab and Fatigue: What Patients Report",
    description: "How ocrelizumab (Ocrevus) infusion timing lines up with fatigue patterns in the days and weeks after dosing.",
    intro: "Ocrelizumab is dosed every six months. Users tracking energy through the infusion cycle report a recognizable pattern in the days and weeks after each dose.",
    keyPoints: [
      "Infusion-day and next-day fatigue are common and usually resolve within 48 hours.",
      "A second, milder dip is often reported 7–14 days post-infusion.",
      "Energy stability in months 3–5 is the typical target zone.",
      "Individual patterns vary, cohort averages are a starting point, not a prescription.",
    ],
    correlation: "In cohort data, the median PROMIS Fatigue-MS 8a score dips 2–4 T-score points in the first 72 hours post-infusion and returns to baseline by week 2.",
    actionable: [
      "Schedule light weeks around infusion days 1–3.",
      "Plan your most demanding personal commitments in months 3–5 of the cycle.",
      "Bring your energy trend to your infusion visit, it's the most useful signal for cadence discussions.",
      "Never adjust dosing without your neurology team.",
    ],
    faqs: [
      { question: "Is fatigue after ocrelizumab a sign the medication isn't working?", answer: "No. Transient fatigue post-infusion is a well-documented, unrelated infusion-reaction pattern. Efficacy is judged over months and MRI, not the day-of-fatigue signal." },
      { question: "Should I move my infusion date based on fatigue?", answer: "Small shifts (a week or two) are common and safe with your team's guidance. Larger shifts are a clinical decision, not a scheduling one." },
    ],
    relatedSlugs: ["natalizumab-and-fatigue", "ms-fatigue-and-sleep"],
  },
  {
    slug: "natalizumab-and-fatigue",
    category: "medication",
    title: "Natalizumab and Fatigue: The 'Wearing-Off' Pattern",
    description: "Why some natalizumab users report an energy dip in the days before their next infusion, and what to log.",
    intro: "Natalizumab (Tysabri) is dosed every four (or six) weeks. Users often describe a 'wearing-off' pattern in the last 3–5 days of the cycle: rising fatigue, mild return of prior symptoms, then rapid recovery after infusion.",
    keyPoints: [
      "Wearing-off is reported by roughly a third of natalizumab users.",
      "Extended-interval dosing (every 6 weeks) is a common conversation for JCV-positive patients.",
      "Objective energy tracking helps your neurology team distinguish wearing-off from disease activity.",
    ],
    correlation: "Cohort users on natalizumab show a small but reliable dip in the 72 hours before infusion, followed by a same-day recovery.",
    actionable: [
      "Log check-ins around infusion week, the pattern will surface in 2–3 cycles.",
      "Bring your energy trend to your infusion visit.",
      "Discuss JCV index results and dosing interval at every visit.",
    ],
    faqs: [
      { question: "Is 'wearing off' the same as a relapse?", answer: "No. Wearing off is a temporary, dose-related pattern that resolves with the next infusion. A relapse is new neurological symptoms lasting more than 24 hours and warrants clinical evaluation." },
      { question: "Can extended-interval dosing help?", answer: "For JCV-positive patients, extended-interval dosing reduces PML risk. Its impact on wearing-off symptoms varies, track the pattern and discuss with your team." },
    ],
    relatedSlugs: ["ocrelizumab-and-fatigue"],
  },
  {
    slug: "hrv-and-ms-fatigue",
    category: "wearable",
    title: "HRV and MS Fatigue: Reading Your Autonomic Signal",
    description: "How heart-rate variability from Oura, Whoop, Apple Watch, or Fitbit tracks autonomic strain, and what it means for your energy.",
    intro: "Heart-rate variability (HRV) measures the beat-to-beat variation in your heart rate. Higher HRV generally means better parasympathetic recovery. For MS users, it's one of the earliest objective signals of autonomic strain.",
    keyPoints: [
      "HRV is a trend, not a snapshot, compare to your own 7-day rolling average, not to strangers.",
      "A 15%+ drop from your baseline usually precedes a subjective energy dip by 24–48 hours.",
      "Illness, alcohol, poor sleep, and heat all suppress HRV overnight.",
      "Not all wearables measure HRV the same way, pick one and stick with it.",
    ],
    correlation: "Users who share wearable data show a −0.35 to −0.55 correlation between overnight HRV drop and next-day fatigue score.",
    actionable: [
      "Track HRV alongside daily check-ins for 30 days before drawing conclusions.",
      "Use HRV as a leading indicator to schedule lighter days, not to justify pushing through.",
      "Watch trends across a week, not day-to-day noise.",
    ],
    faqs: [
      { question: "Which wearable is best for HRV?", answer: "Oura and Whoop measure HRV overnight, which is the most reliable window. Apple Watch and Fitbit sample HRV less frequently but still produce useful trends." },
      { question: "Can HRV predict a relapse?", answer: "No. HRV reflects autonomic and recovery state, not disease activity. Treat any new neurological symptoms as a clinical event regardless of HRV." },
    ],
    relatedSlugs: ["ms-fatigue-and-sleep"],
  },
  {
    slug: "pacing-strategies-for-ms",
    category: "lifestyle",
    title: "Pacing Strategies for MS: Out-Plan the Crash",
    description: "Pacing is the single most-cited fatigue-management tool in MS. Here's what works, what doesn't, and how to build a real plan.",
    intro: "Pacing isn't 'take it easy.' It's spending the energy you have on what matters before the dip arrives, deliberately, on a schedule you set the night before.",
    keyPoints: [
      "Rest before the crash, not after, planned rest keeps the next block usable.",
      "Time-box demanding work into your real energy window (usually morning).",
      "Batch cognitive tasks separately from physical tasks; the fatigue signatures are different.",
      "One protected recovery day per week beats seven half-utilized ones.",
    ],
    correlation: "Cohort users who log a scheduled rest break before their historical crash window report 30% fewer 'worst-day' entries per month.",
    actionable: [
      "Every night, pick your ONE priority for tomorrow's peak window. Move everything else.",
      "Insert a scheduled 20-minute rest 30 minutes before your usual crash time.",
      "Say no to same-day requests during your peak window, that's non-negotiable time.",
    ],
    faqs: [
      { question: "Isn't pacing just giving up?", answer: "No. Pacing is choosing where the energy goes. People who pace deliberately consistently accomplish more meaningful work than people who push and crash." },
      { question: "How is pacing different from resting?", answer: "Resting is reactive, you rest because you crashed. Pacing is proactive, you rest so you don't." },
    ],
    relatedSlugs: ["ms-fatigue-and-sleep", "cooling-strategies-for-ms"],
  },
];

export const getLearnTopic = (slug: string) =>
  LEARN_TOPICS.find((t) => t.slug === slug);

export const LEARN_CATEGORIES: Record<LearnTopicCategory, string> = {
  symptom: "Symptoms",
  medication: "Medications",
  wearable: "Wearables & data",
  lifestyle: "Daily strategies",
};
